import { useLocation } from 'react-router-dom';
import {
  DeployRefreshGuard,
  DEFAULT_DEPLOY_REFRESH_POLL_INTERVAL_MS,
  DEPLOY_REFRESH_META_PATH,
  fetchDeployRefreshBuildMeta,
  hasDifferentDeployRefreshBuildId,
  isDeployRefreshGuardEnabled,
  type DeployRefreshBuildMeta,
  type DeployRefreshGuardProps,
} from '@triedge/app-shell-ui/deploy-refresh';
import styles from './buildRefreshGuard.module.css';

export type BuildRefreshMeta = DeployRefreshBuildMeta;

type YstnjBuildRefreshGuardProps = Omit<
  DeployRefreshGuardProps,
  | 'classNames'
  | 'currentBuildId'
  | 'enabled'
  | 'fetchBuildMeta'
  | 'isAutoReloadRoute'
  | 'messages'
  | 'safeToReloadEventName'
  | 'testId'
> & {
  currentBuildId?: string;
  enabled?: boolean;
  fetchBuildMeta?: () => Promise<BuildRefreshMeta | null>;
};

type BuildRefreshGuardMountProps = {
  enabled?: boolean;
};

export const BUILD_REFRESH_META_PATH = '/build-meta.json';
export const BUILD_REFRESH_SAFE_TO_RELOAD_EVENT = 'ystnj:build-refresh-safe-to-reload';
export const DEFAULT_BUILD_REFRESH_POLL_INTERVAL_MS = DEFAULT_DEPLOY_REFRESH_POLL_INTERVAL_MS;
export const BUILD_REFRESH_GUARD_STORAGE_KEY = 'ystnj.buildRefreshGuard.enabled';

const normalizeBuildId = (value: unknown): string => (
  typeof value === 'string' ? value.trim() : ''
);

const readClientBuildId = (): string => (
  normalizeBuildId(import.meta.env.VITE_YSTNJ_BUILD_ID)
  || normalizeBuildId(import.meta.env.VITE_GIT_COMMIT)
  || 'dev'
);

export const isBuildRefreshGuardEnabled = (): boolean => {
  return isDeployRefreshGuardEnabled({
    currentBuildId: readClientBuildId(),
    production: Boolean(import.meta.env.PROD),
    storageKey: BUILD_REFRESH_GUARD_STORAGE_KEY,
  });
};

export const hasDifferentBuildId = hasDifferentDeployRefreshBuildId;

export const isBuildRefreshAutoReloadRoute = (pathname: string): boolean => {
  const normalized = pathname.split(/[?#]/, 1)[0]?.replace(/\/+$/, '') || '/';
  if (normalized === '/') return true;
  if (normalized === '/auth') return true;
  if (normalized === '/privacy-policy' || normalized === '/terms-conditions') return true;
  if (normalized === '/school/admissions/dashboard') return true;
  if (normalized === '/school/financialaid/dashboard') return true;
  if (normalized === '/parent/portal/dashboard') return true;
  return false;
};

export const fetchBuildRefreshMeta = async (): Promise<BuildRefreshMeta | null> => {
  return fetchDeployRefreshBuildMeta({
    metaPath: BUILD_REFRESH_META_PATH,
  });
};

export const BuildRefreshGuard = ({
  currentBuildId = readClientBuildId(),
  enabled = isBuildRefreshGuardEnabled(),
  fetchBuildMeta = fetchBuildRefreshMeta,
  ...props
}: YstnjBuildRefreshGuardProps) => (
  <DeployRefreshGuard
    {...props}
    classNames={styles}
    currentBuildId={currentBuildId}
    enabled={enabled}
    fetchBuildMeta={fetchBuildMeta}
    isAutoReloadRoute={isBuildRefreshAutoReloadRoute}
    safeToReloadEventName={BUILD_REFRESH_SAFE_TO_RELOAD_EVENT}
    testId="build-refresh-guard-banner"
  />
);

export const BuildRefreshGuardMount = ({ enabled }: BuildRefreshGuardMountProps) => {
  const location = useLocation();
  return (
    <BuildRefreshGuard
      enabled={enabled}
      pathname={location.pathname}
    />
  );
};
